import { FC, useState } from 'react'
import { Box, ListItemText, OutlinedInput, Typography, Select, MenuItem, SelectProps } from '@mui/material'

/* Utils */
import { CPIManifest, IntegrationInstance, IntegrationStatusMessage } from 'models'
import { getIntegrationLogoFromManifest } from '../../../utils/libraryDetailsUtils'

/* Components */
import { TrendsChartSelectDropdownIcon } from 'ui/components/svg'
import { DataSourceInputAdornment, IntegrationItem } from '../../../../library/components/library-components.styles'
import DataSourceErrorTooltip from 'ui/components/data-source-error-tooltip'
import { LibraryDataSourceFormControl, CategoryMenuProps } from './libraryDataSourceSelect.styles'

interface Props extends SelectProps {
  value: any
  handleChange: any
  dataSourceOptions: IntegrationInstance[]
  minWidth?: string
  manifestData: CPIManifest
  customClass?: string
  allOptionText?: string
  raise?: boolean
  formControlMinWidth?: string
  disablePointerEvents?: boolean
  inlineLabel?: string
}

const LibraryDataSourceSelect: FC<Props> = ({
  handleChange,
  placeholder,
  dataSourceOptions,
  minWidth,
  value,
  raise,
  manifestData,
  customClass,
  allOptionText,
  formControlMinWidth,
  disablePointerEvents,
  inlineLabel,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState(null)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <LibraryDataSourceFormControl
      iscustom={customClass ? 'true' : 'false'}
      sx={{ minWidth, opacity: rest.disabled ? 0.38 : 1 }}
      minwidth={formControlMinWidth || ''}
      isselected={placeholder !== 'Sort' && value.length > 1 && !value.includes('All (') ? 'true' : 'false'}
      is_opened={anchorEl ? 'true' : 'false'}
      is_disabled={rest.disabled ? 'true' : 'false'}
    >
      <Select
        labelId="library-data-source-select-label"
        id="library-data-source-select"
        displayEmpty
        MenuProps={{
          ...CategoryMenuProps,
          sx: {
            display: anchorEl ? 'block' : 'none',
            zIndex: raise ? 3001 : ''
          },
          anchorEl,
          open: Boolean(anchorEl),
          onClose: () => setAnchorEl(null)
        }}
        IconComponent={(props) => <TrendsChartSelectDropdownIcon {...props} />}
        renderValue={(selected) => {
          if (Array.isArray(selected)) {
            const pruned = selected.filter((s: string) => s !== '')
            if (pruned.length === 0) {
              return placeholder
            }
            if (pruned.length === 1) {
              return pruned[0]
            }

            return `${pruned[0]} +${pruned.length - 1}`
          }

          return selected
        }}
        inputProps={{ 'aria-label': 'Without label' }}
        {...rest}
        value={value}
        onChange={handleChange}
        input={
          <OutlinedInput
            onMouseDown={handleClick}
            startAdornment={
              <DataSourceInputAdornment position="start">
                {inlineLabel && <span className="inline-filter-label">{inlineLabel}</span>}
                <Box className="adornment-wrapper">
                  {value === allOptionText
                    ? dataSourceOptions
                        .filter((n, i) => n.name !== allOptionText && i < 4)
                        .map((n) => {
                          if (n.syncStatus && n.syncStatus.status === IntegrationStatusMessage.failed) {
                            return (
                              <DataSourceErrorTooltip
                                logoPath={`${getIntegrationLogoFromManifest(n.integration_name, manifestData).replace(
                                  '/icons/rounded/',
                                  ''
                                )}`}
                                instanceName={n.name}
                                id={n.id}
                                logoSize={18}
                                hideLink
                                isTrendsError
                                isCpiPage
                              />
                            )
                          }
                          return (
                            <IntegrationItem key={n.id} active={!n.deleted}>
                              <img
                                src={getIntegrationLogoFromManifest(n.integration_name, manifestData)}
                                alt={n.integration_name}
                                width="18px"
                                height="18px"
                              />
                              <span className="circle"></span>
                            </IntegrationItem>
                          )
                        })
                    : dataSourceOptions
                        .filter((n) => n.name === value)
                        .map((n) => {
                          if (n.syncStatus && n.syncStatus.status === IntegrationStatusMessage.failed) {
                            return (
                              <DataSourceErrorTooltip
                                logoPath={`${getIntegrationLogoFromManifest(n.integration_name, manifestData).replace(
                                  '/icons/rounded/',
                                  ''
                                )}`}
                                instanceName={n.name}
                                id={n.id}
                                logoSize={18}
                                hideLink
                                isTrendsError
                                isCpiPage
                              />
                            )
                          }
                          return (
                            <IntegrationItem key={n.id} active={!n.deleted}>
                              <img
                                src={getIntegrationLogoFromManifest(n.integration_name, manifestData)}
                                alt={n.integration_name}
                                width="18px"
                                height="18px"
                              />
                              <span className="circle"></span>
                            </IntegrationItem>
                          )
                        })}
                  {dataSourceOptions.length > 4 && (
                    <Typography className="sources-count-text">
                      +{value === allOptionText ? dataSourceOptions.length - 4 : dataSourceOptions.length - 2}
                    </Typography>
                  )}
                </Box>
              </DataSourceInputAdornment>
            }
          />
        }
        sx={{
          minWidth,
          pointerEvents: disablePointerEvents ? 'none' : 'auto',
          '& .MuiSelect-icon': {
            right: '14px',
            transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
            transformOrigin: anchorEl ? '50% 45%' : '0 5px'
          }
        }}
      >
        {dataSourceOptions.map((option) =>
          option.name === allOptionText ? (
            <MenuItem key={option.id} value={option.name} onClick={handleClose} disabled={rest.disabled}>
              <Box className="menu-inner">
                <Box className="text-wrapper">
                  <ListItemText primary={option.name} />
                </Box>
                <Box className="image-wrapper">
                  {dataSourceOptions
                    .filter((n, i) => n.name !== allOptionText && i < 4)
                    .map((n) => (
                      <IntegrationItem
                        className="image-wrapper"
                        active={!n.deleted}
                        key={n.id}
                        iserrored={
                          n.syncStatus && n.syncStatus.status === IntegrationStatusMessage.failed ? 'true' : 'false'
                        }
                      >
                        <img
                          src={getIntegrationLogoFromManifest(n.integration_name, manifestData)}
                          alt={n.integration_name}
                          width="18px"
                          height="18px"
                        />
                      </IntegrationItem>
                    ))}
                </Box>
                {dataSourceOptions.length > 4 && (
                  <Typography className="sources-count-text">+{dataSourceOptions.length - 4}</Typography>
                )}
              </Box>

              {value === option.name && (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2.625 7.4375L6.125 10.9375L11.375 3.0625"
                    stroke="#FFA9FC"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </MenuItem>
          ) : (
            <MenuItem key={option.id} value={option.name} onClick={handleClose} disabled={rest.disabled}>
              <Box className="menu-inner">
                <Box className="text-wrapper">
                  <ListItemText primary={option.name} />
                </Box>

                <IntegrationItem
                  className="image-wrapper"
                  active={!option.deleted}
                  iserrored={
                    option.syncStatus && option.syncStatus.status === IntegrationStatusMessage.failed ? 'true' : 'false'
                  }
                >
                  <img
                    src={getIntegrationLogoFromManifest(option.integration_name, manifestData)}
                    alt={option.integration_name}
                    width="18px"
                    height="18px"
                  />
                  <span className="circle-new"></span>
                </IntegrationItem>
              </Box>

              {value === option.name && (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2.625 7.4375L6.125 10.9375L11.375 3.0625"
                    stroke="#FFA9FC"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </MenuItem>
          )
        )}
      </Select>
    </LibraryDataSourceFormControl>
  )
}

export default LibraryDataSourceSelect
